import { jwtDecode } from 'jwt-decode';
import { InvestorAccessPermission } from 'further-types/further-user';
import { UserType } from 'further-types/user';

export type TokenClaims = {
  furtherUserId: string;
  email: string;
  activeUserType: UserType | undefined;
  isAdminUserImpersonatingFurtherUser?: boolean;
  isReadOnlyUser: boolean;
  hasDocumentSigningPermissions: boolean;
  setEmailOverride: (email: string) => void;
};

type Input = {
  overrideToken?: string;
};

export const useTokenClaims = (input?: Input): TokenClaims => {
  const { overrideToken } = input ?? {};
  const token = overrideToken ?? localStorage.getItem('token');

  if (!token) {
    return {
      furtherUserId: '',
      email: '',
      activeUserType: undefined,
      isReadOnlyUser: false,
      hasDocumentSigningPermissions: false,
      setEmailOverride: () => {},
    };
  }

  // When a user changes their own email in the portal, their token will
  // still have their old email until they log out and log back in. To
  // avoid this, we store the new email in localStorage and use it instead
  // of the email from the token. Their token is still valid.
  const emailOverride = localStorage.getItem('furtherUserEmailOverride');
  const setEmailOverride = (email: string) => {
    localStorage.setItem('furtherUserEmailOverride', email);
  };

  const decodedToken = jwtDecode<{
    _id: string;
    email: string;
    activeUserType: UserType;
    investorAccessPermissions: Array<InvestorAccessPermission>;
    isAdminUserImpersonatingFurtherUser?: boolean;
  }>(token);

  const isReadOnlyUser =
    decodedToken.activeUserType === UserType.Investor &&
    Array.isArray(decodedToken.investorAccessPermissions) &&
    decodedToken.investorAccessPermissions.length === 1 &&
    decodedToken.investorAccessPermissions[0] ===
      InvestorAccessPermission.ReadAll;

  const hasDocumentSigningPermissions =
    Array.isArray(decodedToken.investorAccessPermissions) &&
    decodedToken.investorAccessPermissions.includes(
      InvestorAccessPermission.SignDocuments,
    );

  return {
    furtherUserId: decodedToken._id,
    email: emailOverride ?? decodedToken.email,
    activeUserType: decodedToken.activeUserType,
    isAdminUserImpersonatingFurtherUser:
      decodedToken.isAdminUserImpersonatingFurtherUser,
    isReadOnlyUser,
    hasDocumentSigningPermissions,
    setEmailOverride,
  };
};
